import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  Note,
  Briefcase,
  CalendarCheck,
  FileText,
  Receipt,
  PaperPlaneTilt,
  Check,
  X,
  Clock,
  User,
  Trash,
  CaretDown,
  Funnel,
  BellRinging,
} from "phosphor-react";
import AddFollowUpModal from "./AddFollowUpModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Activity type config
const ACTIVITY_TYPES = {
  note: { icon: Note, color: "bg-blue-100 text-blue-700", label: "Note" },
  job: { icon: Briefcase, color: "bg-purple-100 text-purple-700", label: "Job" },
  visit: { icon: CalendarCheck, color: "bg-green-100 text-green-700", label: "Visit" },
  estimate: { icon: FileText, color: "bg-amber-100 text-amber-700", label: "Estimate" },
  invoice: { icon: Receipt, color: "bg-rose-100 text-rose-700", label: "Invoice" },
};

// Filter options
const FILTER_OPTIONS = [
  { value: null, label: "All" },
  { value: "note", label: "Notes" },
  { value: "visit", label: "Visits" },
  { value: "job", label: "Jobs" },
  { value: "estimate", label: "Estimates" },
  { value: "invoice", label: "Invoices" },
];

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'long' }) + ` at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  } catch (e) {
    return "";
  }
};

const getStatusIcon = (item) => {
  const details = item.details || {};
  
  if (item.type === "visit") {
    switch (details.status) {
      case "completed": return <Check size={12} className="text-green-600" />;
      case "canceled": return <X size={12} className="text-red-600" />;
      case "in_progress": return <Clock size={12} className="text-amber-600" />;
      default: return <CalendarCheck size={12} className="text-blue-600" />;
    }
  }
  
  if (item.type === "estimate" || item.type === "invoice") {
    const event = details.event;
    if (event === "sent" || details.status === "sent") return <PaperPlaneTilt size={12} className="text-blue-600" />;
    if (event === "accepted" || details.status === "accepted") return <Check size={12} className="text-green-600" />;
    if (event === "paid" || details.status === "paid") return <Check size={12} className="text-green-600" />;
  }
  
  return null;
};

const renderLink = (item) => {
  if (!item.link) return null;
  
  switch (item.link.type) {
    case "job":
      return (
        <Link 
          to={`/jobs/${item.link.id}`}
          className="text-xs text-[#FF6600] hover:underline"
          data-testid={`timeline-link-job-${item.link.id}`}
        >
          View Job →
        </Link>
      );
    case "visit":
      return (
        <Link 
          to={`/jobs/${item.link.job_id}`}
          className="text-xs text-[#FF6600] hover:underline"
          data-testid={`timeline-link-visit-${item.link.id}`}
        >
          View Job →
        </Link>
      );
    case "estimate":
      return (
        <Link 
          to={`/estimates?id=${item.link.id}`}
          className="text-xs text-[#FF6600] hover:underline"
          data-testid={`timeline-link-estimate-${item.link.id}`}
        >
          View Estimate →
        </Link>
      );
    case "invoice":
      return (
        <Link 
          to={`/invoices?id=${item.link.id}`}
          className="text-xs text-[#FF6600] hover:underline"
          data-testid={`timeline-link-invoice-${item.link.id}`}
        >
          View Invoice →
        </Link>
      );
    default:
      return null;
  }
};

// Timeline Item Component (defined outside main component)
function TimelineItem({ item, onDelete }) {
  const config = ACTIVITY_TYPES[item.type] || ACTIVITY_TYPES.note;
  const Icon = config.icon;
  
  return (
    <div className="flex gap-3 group" data-testid={`timeline-item-${item.id}`}>
      {/* Icon */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full ${config.color} flex items-center justify-center`}>
        <Icon size={16} weight="bold" />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0 pb-4 border-b border-zinc-100 last:border-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className={`text-[10px] ${config.color} border-0`}>
                {config.label}
              </Badge>
              {getStatusIcon(item)}
              <span className="text-xs text-zinc-400">{formatTimestamp(item.timestamp)}</span>
            </div>
            <p className="text-sm text-zinc-700 mt-1">{item.summary}</p>
            
            {/* Author for notes */}
            {item.type === "note" && item.details?.author_name && (
              <div className="flex items-center gap-1 mt-1 text-xs text-zinc-400">
                <User size={12} />
                <span>{item.details.author_name}</span>
              </div>
            )}
            
            {/* Link */}
            <div className="mt-1">
              {renderLink(item)}
            </div>
          </div>
          
          {/* Delete button for notes */}
          {item.type === "note" && onDelete && (
            <Button
              variant="ghost"
              size="sm"
              className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-zinc-400 hover:text-red-500"
              onClick={() => onDelete(item.id)}
              data-testid={`delete-note-${item.id}`}
            >
              <Trash size={14} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CustomerTimeline({ customerId, customerName, onNoteAdded }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  
  const PAGE_SIZE = 50;

  const fetchTimeline = useCallback(async (append = false) => {
    if (!customerId) return;
    
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const skip = append ? items.length : 0;
      let url = `${API}/timeline/customer/${customerId}?skip=${skip}&limit=${PAGE_SIZE}`;
      if (filter) {
        url += `&activity_type=${filter}`;
      }
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        if (append) {
          setItems(prev => [...prev, ...data.items]);
        } else {
          setItems(data.items);
        }
        setTotal(data.total);
        setHasMore(data.has_more);
      }
    } catch (error) {
      console.error("Error fetching timeline:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [customerId, filter, items.length]);

  useEffect(() => {
    fetchTimeline(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, filter]);

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }
    
    setAddingNote(true);
    try {
      const response = await fetch(`${API}/timeline/customer/${customerId}/note`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ note_text: newNote }),
      });
      
      if (response.ok) {
        const newItem = await response.json();
        setItems(prev => [newItem, ...prev]);
        setTotal(prev => prev + 1);
        setNewNote("");
        toast.success("Note added");
        if (onNoteAdded) onNoteAdded();
      } else {
        toast.error("Failed to add note");
      }
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    
    try {
      const response = await fetch(`${API}/timeline/note/${noteId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        setItems(prev => prev.filter(item => item.id !== noteId));
        setTotal(prev => prev - 1);
        toast.success("Note deleted");
      } else {
        toast.error("Failed to delete note");
      }
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  return (
    <div className="space-y-4" data-testid="customer-timeline">
      {/* Add Note Input */}
      <Card className="border-2 border-zinc-200">
        <CardContent className="p-4">
          <div className="space-y-3">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add an internal note about this customer..."
              className="border-2 resize-none"
              rows={2}
              data-testid="add-note-input"
            />
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={() => setFollowUpModalOpen(true)}
                className="border-2 text-zinc-600"
                data-testid="add-followup-btn"
              >
                <BellRinging size={16} className="mr-2" />
                Add Follow-Up
              </Button>
              <Button
                onClick={handleAddNote}
                disabled={addingNote || !newNote.trim()}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                data-testid="add-note-btn"
              >
                <Note size={16} className="mr-2" />
                {addingNote ? "Adding..." : "Add Note"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Follow-Up Modal */}
      <AddFollowUpModal
        open={followUpModalOpen}
        onOpenChange={setFollowUpModalOpen}
        customerId={customerId}
        customerName={customerName || "Customer"}
        onSuccess={() => {
          fetchTimeline(false);
          if (onNoteAdded) onNoteAdded();
        }}
      />

      {/* Filter Chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <Funnel size={16} className="text-zinc-400" />
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value || "all"}
            onClick={() => setFilter(opt.value)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              filter === opt.value
                ? "bg-[#FF6600] text-white border-[#FF6600]"
                : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"
            }`}
            data-testid={`filter-${opt.value || "all"}`}
          >
            {opt.label}
          </button>
        ))}
        {total > 0 && (
          <span className="text-xs text-zinc-400 ml-2">
            {total} {total === 1 ? "item" : "items"}
          </span>
        )}
      </div>

      {/* Timeline List */}
      {loading ? (
        <Card className="border-2 border-zinc-200">
          <CardContent className="p-8 text-center">
            <div className="animate-pulse">
              <div className="h-4 bg-zinc-200 rounded w-1/3 mx-auto mb-2"></div>
              <div className="h-4 bg-zinc-200 rounded w-1/2 mx-auto"></div>
            </div>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="p-8 text-center text-zinc-400">
            <Clock size={32} className="mx-auto mb-2 text-zinc-300" />
            <p>{filter ? `No ${filter}s found` : "No activity yet"}</p>
            <p className="text-xs mt-1">Add a note to get started</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2 border-zinc-200">
          <CardContent className="p-4">
            <div className="space-y-4">
              {items.map((item) => (
                <TimelineItem key={item.id} item={item} onDelete={handleDeleteNote} />
              ))}
            </div>
            
            {/* Load More */}
            {hasMore && (
              <div className="mt-4 text-center">
                <Button
                  variant="outline"
                  onClick={() => fetchTimeline(true)}
                  disabled={loadingMore}
                  className="border-2"
                  data-testid="load-more-btn"
                >
                  <CaretDown size={16} className="mr-2" />
                  {loadingMore ? "Loading..." : `Load More (${total - items.length} remaining)`}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
